import { db } from '../firebase/firebase';
import { collection, getDocs } from 'firebase/firestore';

const membersRef = collection(db, 'members');

/**
 * Some documents in this collection were entered inconsistently:
 * the surname field is sometimes `secondName` and sometimes `lastName`,
 * and `status` is sometimes "Active" and sometimes "active". Firestore's
 * `where('field', '==', value)` is an exact, case-sensitive match, so
 * querying directly on those fields silently drops records that don't
 * match the exact casing/field name used in the query.
 *
 * Until the underlying data is cleaned up, we fetch the members collection
 * once and do the matching client-side, tolerant of both variants. This
 * collection is a membership directory, so it's expected to stay small
 * enough (hundreds, not tens of thousands) for this to be cheap.
 */

function getSurname(member) {
  return member.secondName || member.lastName || '';
}

function isActive(member) {
  return (member.status || '').trim().toLowerCase() === 'active';
}

async function fetchActiveMembers() {
  const snapshot = await getDocs(membersRef);
  return snapshot.docs
    .map((doc) => ({ id: doc.id, ...doc.data() }))
    .filter(isActive);
}

/**
 * Search active members by first/second name substring (case-insensitive).
 * Tolerant of both `secondName` and `lastName` field naming.
 */
export async function searchByName(name) {
  const term = name.trim().toLowerCase();
  const members = await fetchActiveMembers();
  return members.filter((member) =>
    `${member.firstName || ''} ${getSurname(member)}`
      .toLowerCase()
      .includes(term)
  );
}

/**
 * Search active members by membership number (case/format-insensitive,
 * e.g. "nsk050", "NSK 050", "050" all match "NSK-050").
 */
export async function searchByMembershipNumber(number) {
  const normalized = number
    .trim()
    .toUpperCase()
    .replace(/^NSK\s*-?\s*/i, '')
    .replace(/\s+/g, '');
  const members = await fetchActiveMembers();
  return members.filter((member) => {
    const memberNormalized = (member.membershipNumber || '')
      .toUpperCase()
      .replace(/^NSK\s*-?\s*/i, '')
      .replace(/\s+/g, '');
    return memberNormalized === normalized;
  });
}

/**
 * Unified search used by the member verification form: matches either
 * a membership number (e.g. "NSK-050") or a name/name-fragment
 * (e.g. "John", "John Doe").
 */
export async function searchMembers(term) {
  const raw = term.trim();
  if (!raw) return [];

  const looksLikeNumber = /\d/.test(raw) || /^nsk[\s-]?/i.test(raw);

  let results = looksLikeNumber ? await searchByMembershipNumber(raw) : [];

  if (results.length === 0) {
    results = await searchByName(raw);
  }

  const seen = new Set();
  return results.filter((m) => {
    if (seen.has(m.id)) return false;
    seen.add(m.id);
    return true;
  });
}